import PropTypes from "prop-types";
import React, { useEffect } from "react";
import { Row, Col, Alert, Card, CardBody, Container, FormFeedback, Input, Label, Form } from "reactstrap";

//redux
import { useSelector, useDispatch } from "react-redux";
import { createSelector } from "reselect";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import withRouter from "components/Common/withRouter";

// Formik Validation
import * as Yup from "yup";
import { useFormik } from "formik";

// action
import { userForgetPassword, resetPasswordAction } from "../../store/actions";

// import images
import profile from "../../assets/images/profile-img.png";
import logo from "../../assets/images/logo.svg";

const ForgetPasswordPage = props => {

  //meta title
  document.title = "Forget Password | KenGen SMS Portal";

  const dispatch = useDispatch();
  const navigate = useNavigate();

  // The reset email links to /reset-password?token=<43-char>. When a token is
  // present we're in reset mode; otherwise we're requesting the reset email.
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const isResetMode = Boolean(token);

  // Backend password policy (plan 015): min 8 chars with lower/upper/digit/special.
  const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&]).{8,}$/;

  const validation = useFormik({
    // enableReinitialize : use this flag when initial values needs to be changed
    enableReinitialize: true,

    initialValues: {
      email: '',
      password: '',
      confirmPassword: '',
    },
    validationSchema: isResetMode
      ? Yup.object({
          password: Yup.string()
            .required("Please enter a new password")
            .matches(
              PASSWORD_REGEX,
              "At least 8 characters with upper & lower case, a number and a special character."
            ),
          confirmPassword: Yup.string()
            .required("Please confirm your password")
            .oneOf([Yup.ref("password")], "Your password and confirmation password do not match."),
        })
      : Yup.object({
          email: Yup.string().email("Please enter a valid email").required("Please Enter Your Email"),
        }),
    onSubmit: (values) => {
      // Request mode — email a reset link. Success/error surface via the
      // reducer-driven alerts below (the saga does the actual API call).
      if (!isResetMode) {
        dispatch(userForgetPassword(values, props.history));
        return;
      }
      // Reset mode — submit the token from the email link + the new password.
      dispatch(resetPasswordAction({ newPassword: values.password, token }));
    }
  });

  const ForgotPasswordProperties = createSelector(
    (state) => state.ForgetPassword,
    (forgetPassword) => ({
      forgetError: forgetPassword.forgetError,
      resetSuccess: forgetPassword.resetSuccess,
      forgetSuccessMsg: forgetPassword.forgetSuccessMsg,
      resetErrorMsg: forgetPassword.resetErrorMsg,
    })
  );

  const {
    forgetError,
    forgetSuccessMsg,
    resetSuccess,
    resetErrorMsg,
  } = useSelector(ForgotPasswordProperties);

  useEffect(() => {
    if (resetSuccess) {
      navigate('/login');
    }

  }, [resetSuccess])

  return (
    <React.Fragment>
      <div className="home-btn d-none d-sm-block">
        <Link to="/" className="text-dark">
          <i className="bx bx-home h2" />
        </Link>
      </div>
      <div className="account-pages my-5 pt-sm-5">
        <Container>
          <Row className="justify-content-center">
            <Col md={8} lg={6} xl={5}>
              <Card className="overflow-hidden">
                <div className="bg-primary-subtle">
                  <Row>
                    <Col xs={7}>
                      <div className="text-primary p-4">
                        <h5 className="text-primary">Welcome Back !</h5>
                        <p>Sign in to continue to KenGen.</p>
                      </div>
                    </Col>
                    <Col className="col-5 align-self-end">
                      <img src={profile} alt="" className="img-fluid" />
                    </Col>
                  </Row>
                </div>
                <CardBody className="pt-0">
                  <div>
                    <Link to="/">
                      <div className="avatar-md profile-user-wid mb-4">
                        <span className="avatar-title rounded-circle bg-light">
                          <img
                            src={logo}
                            alt=""
                            className="rounded-circle"
                            height="34"
                          />
                        </span>
                      </div>
                    </Link>
                  </div>
                  <div className="p-2">
                    {forgetError && (
                      <Alert color="danger" style={{ marginTop: "13px" }}>
                        {typeof forgetError === "string" ? forgetError : forgetError.message || "An error occurred"}
                      </Alert>
                    )}
                    {resetErrorMsg && (
                      <Alert color="danger" style={{ marginTop: "13px" }}>
                        {typeof resetErrorMsg === "string" ? resetErrorMsg : "Could not reset password — your reset link may have expired. Request a new one."}
                      </Alert>
                    )}
                    {forgetSuccessMsg ? (
                      <Alert color="success" style={{ marginTop: "13px" }}>
                        {forgetSuccessMsg}
                      </Alert>
                    ) : null}

                    {/* Request mode + email sent → confirmation screen, not the
                        form. (Reset mode redirects to /login on success.) */}
                    {!isResetMode && forgetSuccessMsg ? (
                      <div className="text-center mt-4">
                        <p className="text-muted">
                          If an account exists for that email, we&apos;ve sent a password reset link.
                          Open it to choose a new password — the link expires in 1 hour.
                        </p>
                        <Link to="/login" className="btn btn-primary w-md mt-2">
                          Back to Login
                        </Link>
                      </div>
                    ) : (
                    <Form
                      className="form-horizontal"
                      onSubmit={(e) => {
                        e.preventDefault();
                        validation.handleSubmit();
                        return false;
                      }}
                    >
                      {
                        !isResetMode &&
                        <div className="mb-3">
                          <Label className="form-label">Email</Label>
                          <Input
                            name="email"
                            className="form-control"
                            placeholder="Enter email"
                            type="email"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.email || ""}
                            invalid={
                              validation.touched.email && validation.errors.email ? true : false
                            }
                          />
                          {validation.touched.email && validation.errors.email ? (
                            <FormFeedback type="invalid">{validation.errors.email}</FormFeedback>
                          ) : null}
                        </div>
                      }
                      {
                        isResetMode && <>
                          <div className="mb-3">
                            <Label className="form-label">Password</Label>
                            <Input
                              name="password"
                              className="form-control"
                              placeholder="Enter Password"
                              type="password"
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              value={validation.values.password || ""}
                              invalid={
                                validation.touched.password && validation.errors.password ? true : false
                              }
                            />
                            {validation.touched.password && validation.errors.password ? (
                              <FormFeedback type="invalid">{validation.errors.password}</FormFeedback>
                            ) : null}
                          </div>
                          <div className="mb-3">
                            <Label className="form-label">Confirm Password</Label>
                            <Input
                              name="confirmPassword"
                              className="form-control"
                              placeholder="Enter Confirmation Password"
                              type="password"
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              value={validation.values.confirmPassword || ""}
                              invalid={
                                validation.touched.confirmPassword && validation.errors.confirmPassword ? true : false
                              }
                            />
                            {validation.touched.confirmPassword && validation.errors.confirmPassword ? (
                              <FormFeedback type="invalid">{validation.errors.confirmPassword}</FormFeedback>
                            ) : null}
                          </div>
                        </>
                      }
                      <Row className="mb-3">
                        <Col className="text-end">
                          <button
                            className="btn btn-primary w-md "
                            type="submit"
                          >
                            {isResetMode ? "Reset Password" : "Send Reset Link"}
                          </button>
                        </Col>
                      </Row>
                    </Form>
                    )}
                  </div>
                </CardBody>
              </Card>
              <div className="mt-5 text-center">
                <p>
                  Go back to{" "}
                  <Link to="/login" className="font-weight-medium text-primary">
                    Login
                  </Link>
                </p>
                <p>
                  © {new Date().getFullYear()} KenGen.
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

ForgetPasswordPage.propTypes = {
  history: PropTypes.object,
};

export default withRouter(ForgetPasswordPage);
